import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef, useState } from 'react';
import type { ContactPageProps, ContactPageRef } from './ContactPage.data';
import styles from './ContactPage.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './ContactPage.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import HeaderHighlight from '../header-highlight/HeaderHighlight';
import PardotPdfForm, { FormState } from '../pardot-pdf-form/PardotPdfForm';
import type { PardotPdfFormRef } from '../pardot-pdf-form/PardotPdfForm.data';
import EmailForm from '../email-form/EmailForm';
import Script from 'next/script';
import { useRouter } from 'next/router';

export default function ContactPage({
  isVisible,
  className,
  blok,
  ref,
}: ContactPageProps & { ref?: Ref<ContactPageRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const formRef = useRef<PardotPdfFormRef>(null);
  const router = useRouter();

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${process.env.SITE_URL}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Contact',
        item: `${process.env.SITE_URL}/contact`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: blok.seo_title,
        item: `${process.env.SITE_URL}/${router.asPath}`,
      },
    ],
  };

  useChangeTransitionState(transitionController, isVisible);

  const [, setFormLayoutState] = useState(FormState.Default);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.contactPage, className)}
      {...sbEditable(blok)}
    >
      <HeaderHighlight
        blok={{
          title:
            formRef.current?.formLayoutState === FormState.SubmitSuccess
              ? blok.title_submission_success
              : blok.title,
          button: blok.button,
          use_accent_color: false,
          _uid: '',
          component: '',
        }}
      />
      {blok.form[0].component === 'email_form' && (
        <EmailForm
          blok={blok.form[0]}
          ref={formRef}
          parentHandleSubmit={(formState) => setFormLayoutState(formState)}
        />
      )}
      {blok.form[0].component === 'pardot_pdf_form' && (
        <PardotPdfForm
          blok={blok.form[0]}
          ref={formRef}
          parentHandleSubmit={(formState) => setFormLayoutState(formState)}
        />
      )}

      <Script
        id="breadcrumbs-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />
    </div>
  );
}
